<script lang="ts">
  import { Tabs, TabItem, Accordion, AccordionItem } from "flowbite-svelte";
  import type { Document } from "../../../services/api/types.js";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faExternalLink, faTimes, faDownload, faTrash, faChevronDown, faChevronRight,  faFileAlt, faXmark } from "@fortawesome/free-solid-svg-icons";
  import { selectedObjects } from "../../../services/stores/selected-objects-store.js";
  import { getDocumentsByCustomer, getSASUrl } from "../../../services/api/data-handler.js";
  import { getEnvironment } from "../../../config/env.js";
  import { userStore } from "../../../services/stores/user-store.js";
  import { saveUserMetadata } from "../../../services/api/data-handler.js";
  import { get } from "svelte/store";
  import { createEventDispatcher } from "svelte";
  import { logError, logInfo, logWarning } from "../../../services/logging/log-handler.js";

  // Subscribe to userStore for reactivity
  $: $userStore;
  import Chatbot2 from "../chat/Chatbot2.svelte";
  import JSZip from "jszip";
  import { Splitpanes, Pane } from "svelte-splitpanes";
  import { onMount, onDestroy } from "svelte";

  // Helper functions to safely access document properties without type assertions
  function getDocFilename(doc: any): string {
    return doc.doc_filename || doc.file_name || "";
  }

  function getDocPathFromDoc(doc: any): string {
    return doc.doc_path || doc.file_name || doc.doc_filename || "";
  }

  export let customer_id: string;
  export let iframeProp: HTMLIFrameElement | null = null;
  export let sessionToLoad: string | null = null;

  const dispatch = createEventDispatcher();

  let customerCode: string = "";
  let documents: Document[] = [];
  
  // Memoized normalized documents array to prevent Chatbot2 from remounting
  // Only recreate when documents actually change (by content, not reference)
  let normalizedDocuments: Array<{
    doc_key: string;
    file_name: string;
    project_id: string;
    doc_title: string;
    project: string;
    stage: string;
  }> = [];
  
  // Update normalized documents only when documents array content changes
  $: {
  
    const newNormalized = documents.map((d) => {
      // Handle both file_name (old) and doc_filename (new) from API
      const fileName = getDocFilename(d);
      return {
        doc_key: d.doc_key,
        file_name: fileName,
        project_id: d.project_id,
        doc_title: d.doc_title,
        project: d.project,
        stage: d.stage,
      };
    });
    
    // Only update if content actually changed (compare by doc_key)
    const currentKeys = normalizedDocuments.map(d => d.doc_key).sort().join(',');
    const newKeys = newNormalized.map(d => d.doc_key).sort().join(',');
    
    if (currentKeys !== newKeys) {
      normalizedDocuments = newNormalized;
    }
  }

  // Splitter position
  const MIN_WIDTH_REM = 10;

  // Initialize with default calculated value immediately
  function calculateDefaultPosition(): number {
    const viewportWidth = window.innerWidth;
    const remInPixels = 16; // Base font size
    const minWidthPixels = MIN_WIDTH_REM * remInPixels;
    return (minWidthPixels / viewportWidth) * 100;
  }

  let splitPosition: number = calculateDefaultPosition();
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let hasLoadedPosition = false;
  let lastSavedPosition: number | null = null; // Track last saved to avoid duplicate saves
  let pendingSave = false; // Track if there's a pending save

  // Load splitter position from user metadata when user store is ready (shared with ProjectsContent)
  $: {
    if ($userStore.user && !hasLoadedPosition) {
      if ($userStore.user.metadata?.content_context_splitter) {
        const saved = $userStore.user.metadata.content_context_splitter;
        if (typeof saved === "number" && saved > 0 && saved < 100) {
          splitPosition = saved;
          lastSavedPosition = saved;
          hasLoadedPosition = true;
        } else {
          hasLoadedPosition = true;
        }
      } else {
        hasLoadedPosition = true;
      }
    }
  }

  // Actually perform the save
  async function doSave() {
    // Skip if position hasn't changed from last save
    if (lastSavedPosition !== null && Math.abs(splitPosition - lastSavedPosition) < 0.5) {
      pendingSave = false;
      return;
    }
    
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        content_context_splitter: splitPosition,
      };
      await saveUserMetadata(newMetadata);
      lastSavedPosition = splitPosition;
    } catch (error) {
      logError(error, "DocumentsContent", undefined, { message: "Error saving splitter position" });
    } finally {
      pendingSave = false;
    }
  }

  // Save splitter position to user metadata (debounced, shared with ProjectsContent)
  function saveSplitterPosition() {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    pendingSave = true;
    saveTimeout = setTimeout(() => {
      doSave();
      saveTimeout = null;
    }, 1000); // Debounce by 1000ms (increased from 500ms)
  }

  // Handle splitter resize
  function handleSplitterResized(event: CustomEvent) {
    const panes = event.detail as Array<{
      size: number;
      min?: number;
      max?: number;
    }>;
    if (
      panes &&
      Array.isArray(panes) &&
      panes.length > 0 &&
      panes[0]?.size !== undefined
    ) {
      splitPosition = panes[0].size;
      saveSplitterPosition();
    }
  }

  // Tab state management
  let activeTab: "content" | "chat" = "content";
  let currentDocumentId: string | null = null;
  let hoveredDocumentKey: string | null = null;

  // Cache all documents for current customer to avoid refetching on selection changes
  let allDocumentsForCustomer: Document[] = [];
  let lastLoadedCustomerForDocuments: string | null = null;

  // Get document ID for metadata storage
  function getDocumentId(doc: Document): string {
    // Handle both file_name (old) and doc_filename (new) from API
    const fileName = getDocFilename(doc);
    return `${doc.customer_code}_${doc.project_id}_${fileName}`;
  }

  // Load saved tab preference
  function loadTabPreference() {
    const store = get(userStore);
    if (!store.user?.metadata?.document_tab) return;
    const savedTab = store.user.metadata.document_tab;
    if (savedTab === "chat") {
      activeTab = "chat";
    } else {
      activeTab = "content";
    }
  }

  // Save tab preference
  async function saveTabPreference(tab: "content" | "chat") {
    try {
      const store = get(userStore);
      if (!store.user) return;
      const newMetadata = {
        ...store.user.metadata,
        document_tab: tab,
      };
      await saveUserMetadata(newMetadata);
    } catch (error) {
      logError(error, "DocumentsContent", undefined, { message: "Error saving tab preference" });
    }
  }

  // Handle tab change
  async function onTabChange(newTab: "content" | "chat") {
    if (documents.length === 1) {
      activeTab = newTab;
      await saveTabPreference(newTab);
    }
  }

  function loadData() {
    
    if ($selectedObjects.documents.length > 0) {
       if (allDocumentsForCustomer.length > 0) {
        
        // Map selected documents using cached list
        // Match by doc_key - NO FALLBACKS
        documents = $selectedObjects.documents
          .map((selectedDocument) => {
            // Validate doc_key exists
            if (!selectedDocument.doc_key || typeof selectedDocument.doc_key !== "string" || selectedDocument.doc_key.trim() === "") {
              return undefined;
            }
            
            // Match by doc_key only - NO FALLBACKS
            const matched = allDocumentsForCustomer.find(
              (d) => d.doc_key === selectedDocument.doc_key
            );
           
            return matched;
          })
          .filter((document): document is Document => document !== undefined);

        // Set the first document as selected by default
        if (documents.length > 0) {
          customerCode = documents[0].customer_code;

          // Load tab preference for single document
          if (documents.length === 1) {
            loadTabPreference();
          }
        } else {
          logError(new Error("No documents matched! selectedObjects has " + $selectedObjects.documents.length + " but matched 0"), "DocumentsContent", undefined);
        }
      } else {
        // API hasn't returned yet - wait for it to complete before attempting to match
        // This is expected during initial load, so don't log an error
        documents = [];
      }
    } else {
      documents = [];
    }
  }

  // Load cache once per customer_id change
  $: {
    if (customer_id && customer_id !== lastLoadedCustomerForDocuments) {
      getDocumentsByCustomer(customer_id)
        .then((documentsData) => {
          allDocumentsForCustomer = documentsData?.documents || [];
          lastLoadedCustomerForDocuments = customer_id;
          loadData();
        })
        .catch((error) => {
          logError(error, "DocumentsContent", undefined, { message: "Error loading documents" });
          allDocumentsForCustomer = [];
          lastLoadedCustomerForDocuments = customer_id;
          loadData();
        });
    }
  }

  // Recompute visible selection when selected documents change (without refetching)
  $: {
    if ($selectedObjects.documents) {
      loadData();
    }
  }

  function getUrlForOld(doc: Document): string {
    try {
      const frontDoorUrl = getEnvironment().frontDoorUrl;
      // Handle both file_name (old) and doc_filename (new) from API
      const fileName = getDocFilename(doc);
      const baseUrl = `${frontDoorUrl}/${doc.customer_code}/${doc.project_id}/${fileName}`;
      const document_type = (fileName || "")
        .split(".")
        .pop()
        ?.toLowerCase();
      if (!document_type) return baseUrl;
      return document_type === "pdf" ||
        document_type === "png" ||
        document_type === "jpg" ||
        document_type === "jpeg"
        ? baseUrl
        : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(baseUrl)}&theme=light`;
    } catch (e) {
      logError(e, "DocumentsContent", undefined, { message: "Error in getUrlForOld" });
      return ""; // fallback
    }
  }

  async function getUrlFor(doc: Document): Promise<string> {
    try {
      // Handle both file_name (old) and doc_filename (new) from API
      // For SAS URL, we need doc_path (storage name), not doc_filename (display name)
      const storageFileName = getDocPathFromDoc(doc);
      const url = await getSASUrl(
        doc.customer_code,
        doc.project_id,
        storageFileName
      );

      // Handle Office 365 embedding for non-PDF files
      const document_type = (storageFileName || "")
        .split(".")
        .pop()
        ?.toLowerCase();

      if (!document_type) return url;

      return document_type === "pdf" ||
        document_type === "png" ||
        document_type === "jpg" ||
        document_type === "jpeg"
        ? url
        : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}&theme=light`;
    } catch (e) {
      logError(e, "DocumentsContent", undefined, { message: "Error getting document URL" });
      return "";
    }
  }

  function safeGet(data: any, path: string, defaultValue: any = "N/A") {
    try {
      return (
        path
          .split(".")
          .reduce((acc: any, part: string) => acc?.[part], data as any) ??
        defaultValue
      );
    } catch {
      return defaultValue;
    }
  }

  // Get display title for document - use doc_title if available, otherwise derive from file_name
  function getDocumentTitle(document: Document): string {
    const docTitle = document.doc_title?.trim();
    if (docTitle && docTitle !== "") {
      return docTitle;
    }
    // Fallback: derive title from filename (remove extension)
    // Handle both file_name (old) and doc_filename (new) from API
    const fileName = document.file_name || (document as any).doc_filename || "";
    const lastDotIndex = fileName.lastIndexOf(".");
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  }

  // Get doc_key for a document (used for removal from selectedObjects)
  function getDocumentKeyForRemoval(doc: Document): string {
    // Use doc_key for matching - NO FALLBACKS
    if (!doc.doc_key || typeof doc.doc_key !== "string" || doc.doc_key.trim() === "") {
      throw new Error(`Cannot remove document: missing required field 'doc_key'. Document: ${JSON.stringify(doc)}`);
    }
    return doc.doc_key.trim();
  }

  // Handle document removal
  function handleRemoveDocument(doc: Document, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const docKey = getDocumentKeyForRemoval(doc);
    selectedObjects.remove(docKey);
  }

  // Group documents by project
  $: groupedDocuments = documents.reduce(
    (acc, doc) => {
      const projectName = doc.project || "Unnamed Project";
      if (!acc[projectName]) {
        acc[projectName] = [];
      }
      acc[projectName].push(doc);
      return acc;
    },
    {} as Record<string, Document[]>
  );

  // Track open state for projects (using array index like ResultsSearch)
  let projectOpenState: boolean[] = [];

  $: {
    // Initialize project open state array - all expanded by default
    const currentKeys = Object.keys(groupedDocuments);
    if (currentKeys.length > 0) {
      // If array is empty or wrong size, initialize all to true
      if (projectOpenState.length !== currentKeys.length) {
        projectOpenState = Array(currentKeys.length).fill(true);
      }
    } else {
      projectOpenState = [];
    }
  }

  $: sortedProjects = Object.entries(groupedDocuments)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([projectName, docs]) => ({
      projectName,
      documents: docs.sort((a, b) => {
        const titleA = a.doc_title || "";
        const titleB = b.doc_title || "";
        return titleA.localeCompare(titleB);
      }),
    }));

  // Download all documents as ZIP
  async function downloadAllAsZip() {
    const frontDoorUrl = getEnvironment().frontDoorUrl;

    async function fetchWithRetry(
      url: string,
      maxAttempts = 3,
      timeout = 60000
    ): Promise<Blob> {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          const response = await fetch(url, {
            signal: controller.signal,
            credentials: "include",
            method: "GET",
            headers: {
              Accept: "*/*",
            },
            mode: "cors",
          });

          clearTimeout(timeoutId);

          if (!response.ok)
            throw new Error(`HTTP error! status: ${response.status}`);
          return await response.blob();
        } catch (error) {
          logWarn("Attempt " + attempt + " failed", "DocumentsContent", undefined, { error: error });
          if (attempt === maxAttempts) throw error;
          await new Promise((resolve) => setTimeout(resolve, attempt * 2000));
        }
      }
      throw new Error("Failed to fetch after all attempts");
    }

    const files = documents.map(async (doc) => {
      // For SAS URL, we need doc_path (storage name), not doc_filename (display name)
      const storageFileName = getDocPathFromDoc(doc);
      const url = await getSASUrl(
        doc.customer_code,
        doc.project_id,
        storageFileName
      );
      // For download filename, use doc_filename (display name) or file_name, then doc_title
      const displayFileName = getDocFilename(doc) || doc.doc_title || storageFileName;
      return {
        url,
        filename: doc.doc_title || displayFileName,
      };
    });

    const resolvedFiles = await Promise.all(files);
    const zip = new JSZip();

    try {
      const customer_code = documents[0]?.customer_code || "documents";
      const filePromises = resolvedFiles.map(async ({ url, filename }) => {
        const blob = await fetchWithRetry(url);
        zip.file(filename, blob);
      });

      await Promise.all(filePromises);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = zipUrl;
      link.download = `${customer_code} selected-documents.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(zipUrl);
    } catch (error) {
      logError(error, "DocumentsContent", undefined, { message: "Error creating zip" });
    }
  }

  // Clear all selected documents
  function clearAll() {
    selectedObjects.clear();
  }

  // Initialize splitter position on mount (now handled reactively)
  onMount(() => {
    // Position loading is handled by reactive statements above
  });

  onDestroy(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    // Save immediately on destroy if there's a pending save
    if (pendingSave) {
      doSave();
    }
  });
</script>

<div class="document-content-layout">
  <Splitpanes class="my-theme" on:resized={handleSplitterResized}>
    <!-- Left Panel - Context Panel -->
    <Pane size={splitPosition} minSize={((MIN_WIDTH_REM * 16) / window.innerWidth) * 100}>
      <div class="documents-panel">
        <div class="documents-header">
          <div class="sub-tab">CONTEXT</div>
          {#if documents.length > 0}
            <div class="documents-header-actions">
              <button type="button" class="action-button" on:click={downloadAllAsZip} title="Download All" >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              <button type="button" class="action-button" on:click={clearAll} title="Clear All">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          {/if}
        </div>
        <div class="documents-list">
          {#if documents.length > 0}
            <div class="documents-list-label">Documents</div>
            <Accordion multiple={true} flush={true} defaultClass="context-accordion">
              {#each sortedProjects as project, i}
                <AccordionItem
                  defaultClass="context-project-group"
                  bind:open={projectOpenState[i]}
                  tag="div"
                  paddingFlush="p-0"
                >
                  <svelte:fragment slot="arrowup">
                    <span style="display: none;"></span>
                  </svelte:fragment>
                  <svelte:fragment slot="arrowdown">
                    <span style="display: none;"></span>
                  </svelte:fragment>
                  <svelte:fragment slot="header">
                    <div class="context-project-header">
                      {#key `${i}-${projectOpenState[i]}`}
                        <FontAwesomeIcon
                          icon={projectOpenState[i]
                            ? faChevronDown
                            : faChevronRight}
                          class="context-project-toggle-icon"
                        />
                      {/key}
                      <div class="context-project-name">
                        {project.projectName}
                      </div>
                    </div>
                  </svelte:fragment>
                  {#each project.documents as document}
                    <!-- svelte-ignore a11y-no-static-element-interactions -->
                    <div
                      class="document-item"
                      on:mouseenter={() => (hoveredDocumentKey = document.doc_key)}
                      on:mouseleave={() => (hoveredDocumentKey = null)}
                    >
                      <div class="document-item-content">
                        <FontAwesomeIcon icon={faFileAlt} class="document-icon" />
                        <div class="document-title text-value">
                          {getDocumentTitle(document)}
                        </div>
                        {#if hoveredDocumentKey === document.doc_key}
                          <button type="button" class="action-button" on:click={(e) => handleRemoveDocument(document, e)} title="Remove document">
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        {/if}
                      </div>
                    </div>
                  {/each}
                </AccordionItem>
              {/each}
            </Accordion>
          {:else}
            <div class="no-documents">
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
              <div class="placeholder animate-pulse"></div>
            </div>
          {/if}
        </div>
      </div>
    </Pane>

    <!-- Right Panel - Content Panel -->
    <Pane>
      <div class="chat-panel">
        <Tabs
          tabStyle="underline"
          contentClass=" rounded-lg"
          class="flex-nowrap overflow-x-auto tabs-container"
        >
          {#if documents.length === 1}
            <TabItem
              class="results-explorer-tab min-w-max"
              open={activeTab === "content"}
              on:click={() => onTabChange("content")}
            >
              <div slot="title" class="sub-tab">
                VIEW
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a
                  href="javascript:void(0)"
                  on:click={async () => {
                    const url = await getUrlFor(documents[0]);
                    window.open(url, "_blank");
                  }}
                  target="_blank"
                  class="external-link"
                  ><FontAwesomeIcon icon={faExternalLink} /></a
                >
              </div>
              {#await getUrlFor(documents[0])}
                <div class="document-viewer flex items-center justify-center">
                  <div class="animate-pulse">Loading document...</div>
                </div>
              {:then documentUrl}
                <iframe
                  title="document"
                  src={documentUrl}
                  class="document-viewer"
                  frameborder="0"
                  bind:this={iframeProp}
                ></iframe>
              {:catch error}
                <div class="document-viewer flex items-center justify-center">
                  <div class="text-red-500">
                    Error loading document: {error.message}
                  </div>
                </div>
              {/await}
            </TabItem>
          {/if}
          <TabItem
            class="results-explorer-tab min-w-max"
            open={documents.length > 1 || activeTab === "chat"}
            on:click={() => onTabChange("chat")}
          >
            <div slot="title" class="sub-tab">
              CHAT
              <!-- svelte-ignore a11y-invalid-attribute -->
              <a
                href="javascript:void(0)"
                on:click={() => {
                  // Generate the same chat ID that Chatbot2 would use
                  import("../../../services/chat/context-manager.js").then(
                    ({ chatManager }) => {
                      const normalizedDocuments = documents.map((d) => {
                        // Handle both file_name (old) and doc_filename (new) from API
                        const fileName = getDocFilename(d);
                        return {
                          doc_key: d.doc_key,
                          file_name: fileName,
                          project_id: d.project_id,
                          doc_title: d.doc_title,
                          project: d.project,
                          stage: d.stage,
                        };
                      });
                      const ids = normalizedDocuments.map((d) => d.doc_key);
                      const chatId = chatManager.generateChatId(
                        customerCode,
                        "documents",
                        ids,
                      );

                      const url = `${getEnvironment().webappUrl}/chatbot?chatId=${chatId}&customer=${customerCode}&documents=${encodeURIComponent(JSON.stringify(normalizedDocuments))}`;
                      window.open(url, "_blank", "width=800,height=600");

                      // Close chat in main window by deselecting documents
                      selectedObjects.clear();
                    },
                  );
                }}
                class="external-link"
                ><FontAwesomeIcon icon={faExternalLink} /></a
              >
            </div>
            <Chatbot2
              customer_code={customerCode}
              documents={normalizedDocuments}
              projects={[]}
              customer_id={customer_id}
              sessionToLoad={sessionToLoad}
              on:sessionLoaded={() => dispatch("sessionLoaded")}
            />
          </TabItem>
        </Tabs>
      </div>
    </Pane>
  </Splitpanes>
</div>
